import { showErrorAlert } from "./alertHelper";

export type LocationData = {
  latitude: number;
  longitude: number;
  address: string;
  postal_code: string;
};

export async function getCurrentLocation(): Promise<LocationData | null> {
  if (!("geolocation" in navigator)) {
    showErrorAlert("Geolocation is not supported by your browser.");
    return null;
  }

  const permissionStatus = await navigator.permissions
    .query({ name: "geolocation" as PermissionName })
    .catch(() => null);

  if (permissionStatus && permissionStatus.state === "denied") {
    showErrorAlert("Location permission denied. Please allow access to use location-based features.");
    return null;
  }

  let position: GeolocationPosition;
  try {
    position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
    });
  } catch (error: any) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        showErrorAlert("You denied location permission. Please enable it in your browser settings.");
        break;
      case error.POSITION_UNAVAILABLE:
        showErrorAlert("Location information is unavailable.");
        break;
      case error.TIMEOUT:
        showErrorAlert("Location request timed out. Please try again.");
        break;
      default:
        showErrorAlert("An unknown error occurred while fetching location.");
        break;
    }
    return null;
  }

  const { latitude, longitude } = position.coords;
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
    );

    if (!response.ok) {
      showErrorAlert("Failed to fetch address details.");
      return null;
    }

    const data = await response.json();
    const address = data.display_name || "Address not found";
    const postal_code = data.address?.postcode || "Postal code not found";

    return { latitude, longitude, address, postal_code };
  } catch {
    showErrorAlert("Failed to retrieve address from location.");
    return null;
  }
}
